import { ApiUrl, WebUrl } from "./Constant";

/**
 * Opens a game from GameLobbyModal. Uses the in-app game page when iframe is
 * supported; otherwise navigates to /Game/Start (same-tab in APK WebView).
 */
export function launchGameFromLobby({ gameId, isSupportIframe, history, breadcrumbData }) {
    if (isSupportIframe) {
        history.push(`${WebUrl._URL_IFRAME_GAME_PAGE}?gameId=${gameId}`, {
            breadcrumbData,
        });
        return;
    }

    const device = window.innerWidth <= 768 ? "m" : "d";
    const gameStartUrl =
        `${ApiUrl._API_START}?gameId=${gameId}&device=${device}` +
        (window.ReactNativeWebView ? "&isAPK=true" : "");

    if (window.ReactNativeWebView) {
        window.location.href = gameStartUrl;
    } else {
        window.open(gameStartUrl);
    }
}

/**
 * Centralized "can enter game?" guard used by multiple game lists/lobbies.
 *
 * Design goal:
 * - Keep the business rules in one place (promo / oneWallet / freeGGRDisable / login)
 * - Let each caller keep its own `startGame(...)` signature by passing `onProceed()`
 * - Allow different UM flags by passing `umKeys` (e.g. ['gameUm','masterProductUm'] vs ['productUm'])
 */
export function getGameEntryDecision({
    heartBeat,
    isPromoActive,
    game,
    isActiveProductUM = false, // New
    umKeys = ["gameUm", "masterProductUm"],
    forceWithdraw = false,
}) {
    if (!heartBeat) {
        return { type: "LOGIN_REQUIRED" };
    }

    const isUmGame = Array.isArray(umKeys)
        ? umKeys.some((k) => game?.[k] === true)
        : false;

    // isPromotion === false means the game is RESTRICTED by an active promo.
    // isPromotion === true means the game is ALLOWED.
    const isAllowedByPromo = game?.isPromotion != false;
    const freeGGRDisable = game?.freeGGRDisable === true;

    // If the user is force withdraw and the heart beat is true, block the game.
    if (forceWithdraw && heartBeat) {
        return {
            type: "BLOCK_MESSAGE",
            messageKey: "FORCE_WITHDRAW_WARNING",
        };
    }

    // 1. Check for Active Product UM (Maintenance/Block state)
    if (isActiveProductUM || isUmGame) {
        return {
            type: "BLOCK_MESSAGE",
            messageKey: "GAME_UM"
        };
    }

    // 2. Restriction: If it's a non-UM game and NOT in the 'allowed' list 
    // (isPromotion is false), block the user.
    if (!isAllowedByPromo) {
        return {
            type: "BLOCK_MESSAGE",
            messageKey: "PLEASE_END_PROMOTION_TO_CONTINUE",
        };
    }

    // 3. Provider/Game restriction for special GGR games
    if (freeGGRDisable) {
        // If the game is allowed by the promo but has GGR disabled, 
        // it usually implies a conflict with the current promotion type.
        if (isAllowedByPromo && isPromoActive) {
            return {
                type: "BLOCK_MESSAGE",
                messageKey: "PLEASE_END_PROMOTION_TO_CONTINUE",
            };
        }

        // Default fallback for GGR disabled games
        return {
            type: "BLOCK_MESSAGE",
            messageKey: "SPECIAL_GAMES_FOR_PROMOTION",
        };
    }

    return { type: "PROCEED" };
}


/**
 * Executes the decision with the caller's UI/actions.
 *
 * Required params:
 * - `dispatch`: redux dispatch
 * - `showMessage`: action creator
 * - `setLoginModal`: action creator
 * - `t`: i18n translator
 * - `AlertTypes`: constants (needs `_WARNING`)
 * - `onProceed`: callback that launches the game/provider
 *
 * Optional params:
 * - `promoConfirmCustomIcon`: React node for `showMessage({ customIcon })`
 * - `onMarkTransactionValidForPromo`: async/sync callback (usually API call) before proceeding
 * - `decisionOverrides`: pass a precomputed decision (to avoid double compute)
 */
export function runGameEntryGuard({
    decisionOverrides,
    dispatch,
    showMessage,
    setLoginModal,
    AlertTypes,
    customIcon,
    t,
    onProceed,
    ...decisionInput
}) {
    const decision =
        decisionOverrides ??
        getGameEntryDecision({
            ...decisionInput,
        });

    if (decision?.type === "LOGIN_REQUIRED") {
        dispatch(setLoginModal(true));
        return { ok: false, decision };
    }

    if (decision?.type === "BLOCK_MESSAGE") {
        if (decision.messageKey === "GAME_UM") {
            dispatch(
                showMessage(
                    customIcon
                        ? {
                              custom: true,
                              customIcon,
                              content: t("GAME_UM"),
                              onConfirm: () => {},
                          }
                        : {
                              type: AlertTypes._WARNING,
                              content: t("GAME_UM"),
                          }
                )
            );
            return { ok: false, decision };
        }

        dispatch(
            showMessage({
                type: AlertTypes._WARNING,
                content: t(decision.messageKey),
            })
        );
        return { ok: false, decision };
    }

    if (typeof onProceed === "function") {
        onProceed();
    }
    return { ok: true, decision };
}


export function buttonDisabledCheck(
    heartBeat,
    oneWallet,
    walletsDetails,
    game,
    isPromoActive,
) {
    // The button is disabled if:
    // 1. (The game is not a promotion or freeGGRDisable is true) AND user is in oneWallet mode AND heartbeat is true
    // OR
    // 2. oneWallet mode is on AND there's an active promotion AND heartbeat is true
    // OR
    // 3. heartbeat and oneWallet mode are on AND walletsDetails.forceWithdraw is true
    return (
        (
            (game?.isPromotion == false || game?.freeGGRDisable) 
            && oneWallet 
            && heartBeat
        )
        ||
        (
            oneWallet 
            && isPromoActive 
            && heartBeat
            && game == null
        )
        ||
        (
            heartBeat 
            && oneWallet 
            && walletsDetails?.forceWithdraw
        )
    );
}

